import { Data, Logic, Str } from "@cirrus/fe-lib"
import { TextField as MuiTextField } from "@mui/material"
import T from "prop-types"
import { always, and, call, equals, mergeRight, not, or, when } from "ramda"
import { cx } from "ramda-extension/es"
import { createElement as h } from "react"

import { Adornments } from "../../../lib/_private.js"

import * as css from "./css.js"

export const TextField = ({
  FormHelperTextProps,
  background = "secondary",
  backgroundOnHover,
  disableUnderline,
  endAdornment,
  fullWidth,
  hiddenLabel: withoutLabel,
  InputProps = {},
  inputProps = {},
  inputRootClass,
  label,
  params = { InputProps: { className: null } },
  placeholder,
  size = "medium",
  startAdornment,
  type,
  variant = "filled",
  width = 320,
  multipleValues,
  ...props
}) => {
  // TODO!: Remove "legacy" variant after Question Editor's Tags Panel refactored
  const normalizedVariant = variant === "legacy" ? "outlined" : variant

  // TODO!: Add error state for input with "small" and "filled" props
  const hiddenLabel = or(multipleValues, or(equals("small", size), withoutLabel))

  const cssDeps = {
    background,
    backgroundOnHover,
    disableUnderline,
    hiddenLabel,
    multipleValues,
    size,
    variant,
    width,
    ...props,
  }

  return h(MuiTextField, {
    FormHelperTextProps: { ...FormHelperTextProps, classes: css.useFormHelperOverrides() },
    InputLabelProps: {
      ...params.InputLabelProps,
      classes: css.useInputLabelOverrides(),
      size: "small",
    },

    InputProps: Data.merged(
      params.InputProps,
      InputProps,
      Logic.consequence(disableUnderline, equals("filled", variant), { disableUnderline }),

      Adornments.connected({
        endAdornment,
        startAdornment: or(params.startAdornment, startAdornment),
      }),

      {
        className: cx({ [params.InputProps.className]: multipleValues }, inputRootClass),

        classes: call(
          normalizedVariant === "outlined" ? css.useOutlinedInputOverrides : css.useInputOverrides,
          cssDeps,
        ),

        type,
      },
    ),

    classes: css.useRootOverrides(cssDeps),
    fullWidth,
    hiddenLabel,
    id: params.id,
    inputProps: mergeRight(params.inputProps, inputProps),
    label: Logic.consequence(label, not(hiddenLabel)),
    placeholder: when(always(and(props.required, hiddenLabel)), Str.append(" *"), placeholder),
    size,
    variant: normalizedVariant,

    ...props,
  })
}

TextField.propTypes = {
  FormHelperTextProps: T.object,
  /**
   * Props applied to the Input element.
   * It will be a [`FilledInput`](https://material-ui.com/api/filled-input/),
   * [`OutlinedInput`](https://material-ui.com/api/outlined-input/),
   * or [`Input`](https://material-ui.com/api/input/) component
   * depending on the `variant` prop value.
   */
  InputProps: T.object,
  /** Custom css background value. */
  background: T.string,
  /** Custom css background value for the hovered state. */
  backgroundOnHover: T.string,

  disableUnderline: T.bool,
  disabled: T.bool,
  endAdornment: T.oneOfType([T.elementType, T.node]),
  fullWidth: T.bool,
  height: T.oneOfType([T.number, T.string]),
  hiddenLabel: T.bool,
  /**
   * Attributes applied to the input element.
   */
  inputProps: T.object,
  inputRootClass: T.string,
  label: T.string,
  multiline: T.bool,
  multipleValues: T.bool,
  onChange: T.func,
  onKeyDown: T.func,

  /** Utility prop to receive prop overrides from the parent component. */
  params: T.shape(TextField.propTypes),

  placeholder: T.string,
  rows: T.number,

  /** Small-sized text fields have no labels. */
  size: T.oneOf(["medium", "small"]),

  startAdornment: T.oneOfType([T.elementType, T.node]),
  type: T.string,
  variant: T.oneOf(["outlined", "contained", "filled", "legacy"]),
  width: T.oneOfType([T.number, T.string]),
}
